<script lang="ts">
	import type { Icon as IconAdvertisingType, ImageAlt } from '$lib/constants/advertising';
	import Car1 from '$assets/itinerary/car-1.svg';
	import Car2 from '$assets/itinerary/car-2.svg';
	import Car3 from '$assets/itinerary/car-3.svg';
	import ObfuscatedLink from '$lib/components/atoms/obfuscatedLink/ObfuscatedLink.svelte';
	import Icon from '$lib/components/icons/Icon.svelte';
	import { onMount as onMountComponent } from 'svelte';

	const carImages = [Car1, Car2, Car3];

	interface Props {
		url?: string;
		text?: string;
		icon: IconAdvertisingType;
		imagesAlt: ImageAlt[];
		onMount?: () => void;
		onCtaClick?: () => void;
	}

	const {
		url,
		text,
		icon,
		imagesAlt,
		onMount,
		onCtaClick,
	}: Props = $props();

	onMountComponent(() => onMount?.());

	const imagesUrls = $derived(imagesAlt?.map((image, index) => ({
		...image,
		url: carImages[index],
	})));
</script>

<ObfuscatedLink
	target="_blank"
	onclick={() => onCtaClick?.()}
	href={url}
	class="cursor-pointer text-black relative min-h-26 flex items-center justify-center rd-2 text-xl bg-white border-0.5 border-solid border-base-10"
>
	{#if imagesUrls?.length}
		<div class="absolute h-full w-full overflow-hidden">
			{#each imagesUrls as image, index (image.url)}
				<img src={image.url} alt={image.alt} class="absolute animated-car animated-car{index + 1}" />
			{/each}
		</div>
	{/if}
	{#if icon && text}
		<div class="bg-radial w-full h-full z-1 flex items-center justify-center py-5">
			<div class="flex flex-col">
				{#if icon}
					<Icon
						icon={icon?.name}
						ariaLabel={icon?.ariaLabel}
						class="ml-1 text-current text-2xl text-center w-full mb-1"
					/>
				{/if}
				{#if text}
					<span class="text-md font-400 text-center w-20">{text}</span>
				{/if}
			</div>
		</div>
	{/if}
</ObfuscatedLink>

<style>
	@keyframes moveCar1 {
		0% {
			left: -30px;
			top: 120px;
			opacity: 1;
		}
		33% {
			left: 80px;
			top: -100px;
			opacity: 1;
		}
		33.1% {
			opacity: 0;
		}
		100% {
			opacity: 0;
		}
	}

	@keyframes moveCar2 {
		0% {
			opacity: 0;
		}
		36% {
			opacity: 0;
		}
		36.1% {
			left: 180px;
			top: 120px;
			opacity: 1;
		}
		70% {
			left: 110px;
			top: -80px;
			opacity: 1;
		}
		70.1% {
			opacity: 0;
		}
		100% {
			opacity: 0;
		}
	}

	@keyframes moveCar3 {
		0% {
			opacity: 0;
		}
		75% {
			opacity: 0;
		}
		75.1% {
			left: 190px;
			top: -80px;
			opacity: 1;
		}
		100% {
			left: 40px;
			top: 120px;
			opacity: 1;
		}
	}

	.animated-car {
		width: 22px;
		animation-duration: 4s;
		animation-timing-function: ease-out;
		animation-iteration-count: infinite;
	}

	.animated-car1 {
		transform: rotate(30deg);
		animation-name: moveCar1;
	}

	.animated-car2 {
		transform: rotate(-20deg);
		animation-name: moveCar2;
	}

	.animated-car3 {
		transform: rotate(-140deg);
		animation-name: moveCar3;
	}

	.bg-radial {
		background: radial-gradient(
			50% 50% at 50% 50%,
			rgba(255, 255, 255, 0.8) 0%,
			rgba(255, 255, 255, 0.6) 49.98%,
			rgba(255, 255, 255, 0) 100%
		);
	}
</style>
